import * as Tabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface PropsRecuadro {
  titulo?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Agrupador de campos dentro de un formulario. Es el recuadro con borde que usa el
 * sistema actual para separar productor, transporte, cereal y pesos.
 */
export function Recuadro({ titulo, children, className }: PropsRecuadro) {
  return (
    <fieldset
      className={cn('mb-3 rounded border border-slate-300 bg-white px-3 pt-2 pb-3', className)}
    >
      {titulo ? (
        <legend className="px-1 text-sm font-semibold text-marca-700">{titulo}</legend>
      ) : null}
      {children}
    </fieldset>
  );
}

export interface PropsBarraAcciones {
  children: ReactNode;
  className?: string;
}

/** Barra superior de los listados: Nuevo, Editar, Ver, Anular. */
export function BarraAcciones({ children, className }: PropsBarraAcciones) {
  return (
    <div
      role="toolbar"
      aria-label="Acciones"
      className={cn(
        'flex flex-wrap items-center gap-1.5 rounded border border-slate-300 bg-slate-50 p-1.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface DefinicionPestania {
  valor: string;
  etiqueta: string;
  contenido: ReactNode;
  /** Numero o texto corto a la derecha de la etiqueta. */
  insignia?: ReactNode;
}

export interface PropsPestanias {
  pestanias: readonly DefinicionPestania[];
  valor: string;
  onValorChange: (valor: string) => void;
}

export function Pestanias({ pestanias, valor, onValorChange }: PropsPestanias) {
  return (
    <Tabs.Root value={valor} onValueChange={onValorChange}>
      <Tabs.List className="flex flex-wrap gap-0.5 border-b border-slate-300">
        {pestanias.map((pestania) => (
          <Tabs.Trigger
            key={pestania.valor}
            value={pestania.valor}
            className={cn(
              'flex items-center gap-1.5 rounded-t border border-b-0 border-transparent px-3 py-1.5 pointer-coarse:py-2.5',
              'text-sm text-slate-600 hover:text-marca-700',
              'data-[state=active]:border-slate-300 data-[state=active]:bg-white',
              'data-[state=active]:font-semibold data-[state=active]:text-marca-700',
            )}
          >
            {pestania.etiqueta}
            {pestania.insignia !== undefined ? (
              <span className="rounded-full bg-slate-200 px-1.5 text-xs text-slate-700">
                {pestania.insignia}
              </span>
            ) : null}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      {pestanias.map((pestania) => (
        <Tabs.Content
          key={pestania.valor}
          value={pestania.valor}
          className="rounded-b border border-t-0 border-slate-300 bg-white p-3 focus:outline-none"
        >
          {pestania.contenido}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}

export interface PropsTarjetaDato {
  etiqueta: string;
  valor: ReactNode;
  leyenda?: ReactNode;
  tono?: 'neutro' | 'positivo' | 'negativo';
  /** Franja de color a la izquierda: da identidad a cada dato sin recargar la tarjeta. */
  acento?: 'marca' | 'exito' | 'aviso' | 'error';
}

const TONOS: Record<NonNullable<PropsTarjetaDato['tono']>, string> = {
  neutro: 'text-slate-900',
  positivo: 'text-[var(--color-exito)]',
  negativo: 'text-[var(--color-error)]',
};

const ACENTOS: Record<NonNullable<PropsTarjetaDato['acento']>, string> = {
  marca: 'border-l-4 border-l-marca-500',
  exito: 'border-l-4 border-l-emerald-500',
  aviso: 'border-l-4 border-l-amber-500',
  error: 'border-l-4 border-l-red-500',
};

/** Dato destacado de cabecera: saldo en pesos, saldo en dolares, stock total. */
export function TarjetaDato({
  etiqueta,
  valor,
  leyenda,
  tono = 'neutro',
  acento,
}: PropsTarjetaDato) {
  return (
    <div
      className={cn(
        'rounded border border-slate-300 bg-white px-3 py-2',
        acento && ACENTOS[acento],
      )}
    >
      <p className="text-xs tracking-wide text-slate-500 uppercase">{etiqueta}</p>
      <p className={cn('text-xl font-bold tabular', TONOS[tono])}>{valor}</p>
      {leyenda ? <p className="text-xs text-slate-500">{leyenda}</p> : null}
    </div>
  );
}

export interface PropsAviso {
  tono: 'info' | 'advertencia' | 'error';
  children: ReactNode;
}

const AVISOS: Record<PropsAviso['tono'], string> = {
  info: 'border-marca-300 bg-marca-50 text-marca-900',
  advertencia: 'border-amber-300 bg-amber-50 text-amber-900',
  error: 'border-red-300 bg-[var(--color-error-fondo)] text-[var(--color-error)]',
};

export function Aviso({ tono, children }: PropsAviso) {
  return (
    <div
      role={tono === 'error' ? 'alert' : 'status'}
      className={cn('rounded border px-3 py-2 text-sm', AVISOS[tono])}
    >
      {children}
    </div>
  );
}
